import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "@/shared/lib/router/router";
import { useAuth } from "@/shared/model/authContext";
import { useTheme } from "@/shared/model/ThemeContext";
import { useToast } from "@/shared/ui/toast/ToastContext";
import { meService } from "@/entities/user/api/me";
import { setLocale } from "@/shared/i18n";
import { getApiErrorMessage } from "@/shared/lib/errorMessage";

type ThemeMode = "light" | "dark";
type Language = "pt-PT" | "en";

// Apenas estas chaves são geridas/persistidas pelo front. A estrutura do bloco
// `preferences` é responsabilidade do front (ver docs do backend). Mantemos uma
// whitelist explícita para nunca reenviar lixo que o backend possa devolver no
// GET /me (ex.: getters internos do JsonNode — `nodeType`, `containerNode`, …),
// que causariam 500 no PUT. Novas preferências entram aqui quando implementadas.
export interface UserPreferences {
  theme: ThemeMode;
  language: Language;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: "light",
  language: "pt-PT",
};

function normalizeTheme(value: unknown): ThemeMode {
  return value === "dark" ? "dark" : "light";
}

function normalizeLanguage(value: unknown): Language {
  return value === "en" ? "en" : "pt-PT";
}

// Constrói um bloco limpo só com as chaves geridas, normalizadas. Ignora
// quaisquer outras chaves vindas do backend.
function mergePreferences(raw: Record<string, unknown> | null | undefined): UserPreferences {
  const source = raw ?? {};
  return {
    theme: normalizeTheme(source.theme),
    language: normalizeLanguage(source.language),
  };
}

interface UserPreferencesContextType {
  preferences: UserPreferences;
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ) => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userProfile, isAuthenticated } = useAuth();
  const { setTheme } = useTheme();
  const toast = useToast();
  const router = useRouter();

  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);

  // A hidratação só deve correr quando a autenticação muda, não a cada navegação:
  // o ref dá acesso ao router atual sem colocá-lo nas dependências.
  const routerRef = useRef(router);
  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  // Hidratação: backend vence sobre o estado local após autenticação.
  useEffect(() => {
    if (!isAuthenticated || !userProfile) return;

    const merged = mergePreferences(userProfile.preferences);
    setPreferences(merged);
    setTheme(merged.theme);

    const currentRouter = routerRef.current;
    if (currentRouter.locale !== merged.language) {
      setLocale(merged.language);
      void currentRouter.replace(currentRouter.asPath, currentRouter.asPath, { locale: merged.language });
    }
  }, [isAuthenticated, userProfile, setTheme]);

  // Aplica o efeito colateral de uma preferência (DOM / locale).
  const applyEffect = useCallback(
    (key: keyof UserPreferences, value: unknown) => {
      if (key === "theme") {
        setTheme(normalizeTheme(value));
        return;
      }
      if (key === "language") {
        const lang = normalizeLanguage(value);
        setLocale(lang);
        if (router.locale !== lang) {
          void router.replace(router.asPath, router.asPath, { locale: lang });
        }
      }
    },
    [router, setTheme],
  );

  const updatePreference = useCallback(
    async <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      const previous = preferences;
      const next: UserPreferences = { ...previous, [key]: value };

      // Otimista: aplica memória + efeito antes do request.
      setPreferences(next);
      applyEffect(key, value);

      // Sem sessão (login, convite) a escolha vale só neste navegador; o /me recusaria.
      if (!isAuthenticated) return;

      try {
        await meService.updateMe({ preferences: next as unknown as Record<string, unknown> });
      } catch (error) {
        // Rollback: restaura memória e efeito ao estado anterior.
        setPreferences(previous);
        applyEffect("theme", previous.theme);
        applyEffect("language", previous.language);
        toast.error(getApiErrorMessage(error, "Não foi possível guardar a preferência."));
      }
    },
    [preferences, applyEffect, toast, isAuthenticated],
  );

  const value = useMemo(
    () => ({ preferences, updatePreference }),
    [preferences, updatePreference],
  );

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const ctx = useContext(UserPreferencesContext);
  if (ctx === undefined) {
    throw new Error("useUserPreferences must be used within a UserPreferencesProvider");
  }
  return ctx;
};
